@if (!pageDataLoaded()) {
  <app-page-loading-state [loadingState]="loadingState"></app-page-loading-state>
}

@if (
  {
    userOrigin: userOrigin$ | async,
    evalOrigin: evaluationOrigin$ | async,
  };
  as data
) {
  <div class="row flex-column" [class.hide]="!pageDataLoaded()">
    <!-- issue details card -->
    <div class="row col-12">
      @if (issue) {
        <div class="col-xl-8">
          <div class="card">
            <div class="card-header">
              <div class="row">
                <div class="col-sm-12 col-md-8 d-flex flex-column">
                  <h2>{{ 'findings_details' | translate }}</h2>
                </div>
                <div class="col-sm-12 col-md-4 d-flex justify-content-end align-items-center">
                  @if ((userOrigin$ | async) === Origin.staff) {
                    @if (issue.isFlagged) {
                      <div
                        id="flag-status"
                        class="d-flex flex-row gap-2"
                        *appHasPermissions="RequiredSecurities.MT_Manual_Finding_Review_Read"
                      >
                        <ds-icon class="flag" [icon]="Icons.Flag" [iconStyle]="IconStyles.Solid"></ds-icon>
                        {{ 'flagged' | translate }}
                      </div>
                    }
                  }
                  <span class="margin-left-sm me-3">
                    <app-share-issue-report-url [url]="issueShortLink"></app-share-issue-report-url>
                  </span>
                  @if (linkedTask) {
                    <div class="me-2 linked-task">
                      <span class="fw-bold me-2">{{ 'linked_to_task' | translate }}:</span>
                      <a class="btn btn-primary" [routerLink]="taskLink">
                        {{ linkedTask.taskProjectId }}
                      </a>
                    </div>
                  } @else {
                    @if (
                      issueStatus.value !== AuditFindingQAStatus.dismissed && issueStatus.value !== AuditFindingQAStatus.toReview
                    ) {
                      <div class="me-2">
                        <button
                          *appHasPermissions="tasksCreateRequiredSecurity"
                          class="btn btn-primary fw-bold d-flex create-task-button"
                          (click)="openCreateTaskSlideout()"
                        >
                          {{ 'label_create_task' | translate }}
                        </button>
                      </div>
                    }
                  }
                </div>
              </div>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-12 d-flex flex-column">
                  <h2 class="margin-bottom-xs" data-e2e-id="issue-summary">
                    {{ digitalPropertyKey }}&ndash;{{ issue[$auditFinding.issueId] }} &ndash; {{ issue[$auditFinding.summary] }}
                  </h2>
                  <div>
                    <span class="label">{{ 'label_uploaded_from_audit' | translate }}</span>
                    <span class="margin-left-sm">{{ auditName }}</span>
                  </div>
                </div>
                <div class="col-12 d-flex margin-top gap-2 align-items-center">
                  <div id="statusBox">
                    @if (hasStatusEditPermissions) {
                      <div ngbDropdown class="d-inline-block" data-test-id="status-dropdown">
                        <button
                          class="btn btn-primary"
                          id="dropdownBasic1"
                          [attr.aria-label]="
                            'manual_audit_issue_comment_status_change_status_aria_label' | translate: [issueStatus.displayName]
                          "
                          [disabled]="data.userOrigin !== data.evalOrigin"
                          ngbDropdownToggle
                        >
                          {{ issueStatus.displayName }}
                        </button>
                        <div ngbDropdownMenu class="dropdown-menu" aria-labelledby="dropdownBasic1" role="listbox">
                          @for (someStatus of allRegularRestoreStatuses$ | async; track someStatus) {
                            <button
                              ngbDropdownItem
                              class="dropdown-item"
                              role="option"
                              [attr.data-status]="someStatus.value"
                              [attr.aria-selected]="isIssueStatusSelected(someStatus.value)"
                              (click)="openRestoreModalOrUpdateQAIssueStatus(someStatus)"
                            >
                              {{ someStatus.displayName }}
                            </button>
                          }
                          @if (issueStatus.value !== AuditFindingQAStatus.dismissed) {
                            <button
                              ngbDropdownItem
                              class="dropdown-item"
                              role="option"
                              [attr.data-status]="AuditFindingQAStatus.dismissed"
                              [attr.aria-selected]="isIssueStatusSelected(AuditFindingQAStatus.dismissed)"
                              [disabled]="SharedCommonUtility.notNullish(linkedTask)"
                              (click)="openDismissModal()"
                            >
                              {{ dismissedStatus.displayName }}
                            </button>
                          }
                        </div>
                      </div>
                    } @else {
                      <div class="btn btn-outline-secondary disabled uneditable-status-box d-inline-block">
                        {{ issueStatus.displayName }}
                      </div>
                    }
                  </div>
                  @if (hasStatusEditPermissions && data.userOrigin === data.evalOrigin) {
                    <div id="editIssue">
                      <button
                        type="button"
                        class="btn btn-outline-primary fw-bold d-flex"
                        (click)="openEditIssueSlideout($event)"
                      >
                        <svg viewBox="0 0 8 8" class="edit-icon">
                          <use xlink:href="#pencil"></use>
                        </svg>
                        {{ 'label_edit_issue' | translate }}
                      </button>
                    </div>
                  }
                  <div id="delete-issue" *appHasPermissions="RequiredSecurities.MT_Manual_Evaluation_Delete">
                    @if (data.userOrigin === data.evalOrigin) {
                      <button
                        ds-button
                        class="delete-button"
                        [variant]="DsButtonVariants.microAction"
                        [microActionIcon]="Icons.TrashCan"
                        (click)="deleteIssue()"
                        [attr.aria-label]="'delete_finding' | translate"
                      >
                        {{ 'label_delete' | translate }}
                      </button>
                    }
                  </div>
                  @if ((userOrigin$ | async) === Origin.staff) {
                    <div id="change-flag" *appHasPermissions="RequiredSecurities.MT_Manual_Finding_Review_Create">
                      <button
                        ds-button
                        [variant]="DsButtonVariants.microAction"
                        [microActionIcon]="Icons.Flag"
                        (click)="openChangeFlagModal(issue.isFlagged)"
                        [attr.aria-label]="(issue.isFlagged ? 'remove_flag' : 'add_flag') | translate"
                      >
                        {{ (issue.isFlagged ? 'remove_flag' : 'add_flag') | translate }}
                      </button>
                    </div>
                  }
                  @if (hasHistory$ | async) {
                    <app-view-flaw-status-history [issueSource]="manualAuditHistoryRequest"></app-view-flaw-status-history>
                  }
                  @if (showDismissRestore && data.userOrigin === data.evalOrigin) {
                    <div class="margin-left-sm">
                      @if (issueStatus.value !== AuditFindingQAStatus.dismissed) {
                        <button type="button" class="btn btn-link dismiss-btn-link fw-bold" (click)="openDismissModal()">
                          {{ 'label_dismiss_button' | translate }}
                        </button>
                      } @else {
                        <button
                          type="button"
                          id="restoreButton"
                          class="btn btn-link fw-bold"
                          (click)="openRestoreModalOrUpdateQAIssueStatus(openStatus)"
                        >
                          {{ 'label_restore_button' | translate }}
                        </button>
                      }
                    </div>
                  }
                </div>
                <div class="col-12 margin-top">
                  @if (linkedTask && showTaskStatusChangedBanner) {
                    <app-task-status-changed-banner
                      [task]="linkedTask"
                      (closeBanner)="showTaskStatusChangedBanner = false"
                    ></app-task-status-changed-banner>
                  }
                </div>
              </div>
              <div class="row margin-bottom-lg">
                <div class="col-sm-12 col-md-12 d-flex flex-column">
                  <div class="row margin-top-md">
                    <div class="col-md-12">
                      <div class="row">
                        <div class="col-md-6 d-flex flex-column">
                          <div class="d-flex flex-column">
                            <span class="label">{{ 'label_screen' | translate }}</span>
                            <span [innerHTML]="issue[$auditFinding.screen] | linkify"></span>
                          </div>
                          <div class="d-flex flex-column margin-top-md">
                            <span class="label">{{ 'label_url' | translate }}</span>
                            <span [innerHTML]="issueUrl | linkify"></span>
                          </div>
                          @if (issue[$auditFinding.component]) {
                            <div class="d-flex flex-column margin-top-md" data-test-id="component-data-section">
                              <span class="label">{{ 'component' | translate }}</span>
                              <span>{{ issue[$auditFinding.component]?.name }}</span>
                            </div>
                          }
                          <div class="d-flex flex-column margin-top-md">
                            <span class="label">{{ 'issue_field_issueFrequency' | translate }}</span>
                            <span>{{ issue[$auditFinding.issueFrequency] | titlecase }}</span>
                          </div>
                          <div class="d-flex flex-column margin-top-md">
                            <span class="label">{{ 'label_effort' | translate }}</span>
                            <span>{{ issue[$auditFinding.functionalImpact] | titlecase }}</span>
                          </div>
                          @if (issue[$auditFinding.viewport]) {
                            <div class="d-flex flex-column margin-top-md">
                              <span class="label">{{ 'viewport_resolution_px_label' | translate }}</span>
                              <span data-test-id="viewport-size"
                                >{{ issue[$auditFinding.viewport][$auditFindingViewport.width] }} x
                                {{ issue[$auditFinding.viewport][$auditFindingViewport.height] }}</span
                              >
                            </div>
                          }
                        </div>
                        <div class="col-md-6 d-flex flex-column">
                          @if (issue[$auditFinding.severity] && customSeveritiesData) {
                            <div class="d-flex flex-column">
                              <span class="label">{{ 'label_severity' | translate }}</span>
                              <div class="" data-test-id="rule-info-severity">
                                <table-cell-custom-severity
                                  [config]="{
                                    severity: this.issue[$auditFinding.severity],
                                    customSeverity: this.customSeveritiesData.get(this.issue[$auditFinding.severity]),
                                    customSeverityKeysSortedByRank: this.customSeverityKeysSortedByRank,
                                  }"
                                ></table-cell-custom-severity>
                              </div>
                            </div>
                          }
                          @if (issue[$auditFinding.instances]) {
                            <div class="d-flex flex-column margin-top-md" data-test-id="instances">
                              <span class="label">{{ 'label_instances' | translate }}</span>
                              <span [innerHTML]="issue[$auditFinding.instances] | linkify"></span>
                            </div>
                          }
                          @if (issue[$auditFinding.elementReferenceType] === AuditFindingReferenceType.xpath) {
                            <div class="d-flex flex-column gap-1 margin-top-md" data-e2e-id="copy-xpath">
                              <div class="d-flex flex-row justify-content-between">
                                <span class="label">{{ 'label_xpath_or_css' | translate }}</span>
                                <button
                                  ds-button
                                  [variant]="DsButtonVariants.microAction"
                                  [microActionIcon]="Icons.Copy"
                                  [attr.aria-label]="'copy_xpath_or_css_selector_to_clipboard' | translate"
                                  (click)="copyXpathToClipboard()"
                                >
                                  {{ 'copy_to_clipboard' | translate }}
                                </button>
                              </div>
                              <pre><code class="d-block p-2" [appHighlight]="issue[$auditFinding.elementReference]" type="xquery"></code></pre>
                            </div>
                          }
                          @if (issue[$auditFinding.element]) {
                            <div class="d-flex flex-column gap-1 margin-top-md">
                              <div class="d-flex flex-row justify-content-between">
                                <span class="label">{{ 'scan_report_code_snippet' | translate }}</span>
                                <button
                                  ds-button
                                  [variant]="DsButtonVariants.microAction"
                                  [microActionIcon]="Icons.Copy"
                                  (click)="copyElementToClipboard()"
                                >
                                  {{ 'copy_snippet_to_clipboard' | translate }}
                                </button>
                              </div>
                              <pre
                                data-e2e-id="code-snippet"
                              ><code class="d-block p-2" [appHighlight]="issue[$auditFinding.element]" type="html"></code></pre>
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row margin-bottom-lg">
                <div class="col-md-12 d-flex flex-column">
                  <div class="row margin-bottom-xs">
                    <div class="col-md-12">
                      <h2 class="section-title">{{ 'label_what_is_the_issue' | translate }}</h2>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6 d-flex flex-column">
                      <div class="d-flex flex-column">
                        <h3 class="h4 fw-bold">{{ 'label_actual_result' | translate }}</h3>
                        <p class="whitespace-pre-wrap" [innerHTML]="issue[$auditFinding.actualResult] | linkify"></p>
                      </div>
                    </div>
                    <div class="col-md-6 d-flex flex-column">
                      @if (showStepsToReproduce) {
                        <div class="d-flex flex-column margin-top-md" data-e2e-id="steps-to-reproduce-section">
                          <h3 class="h4 fw-bold">{{ 'label_STR' | translate }}</h3>
                          <p class="whitespace-pre-wrap" [innerHTML]="issue[$auditFinding.stepsToReproduce] | linkify"></p>
                        </div>
                      }
                      @if (showUserImpact) {
                        <div class="d-flex flex-column margin-top-md" data-test-id="user-impact-section">
                          <h3 class="h4 fw-bold">{{ 'label_user_impact' | translate }}</h3>
                          <p class="whitespace-pre-wrap" [innerHTML]="issue[$auditFinding.userImpact] | linkify"></p>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
              <div class="row margin-bottom-lg">
                <div class="col-md-12 d-flex flex-column">
                  <div class="row">
                    <div class="col-md-12">
                      <h2 class="section-title">{{ 'label_how_to_resolve' | translate }}</h2>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12 d-flex flex-column">
                      <div class="d-flex flex-column">
                        <h3 class="h4 fw-bold">{{ 'recommendation_comment' | translate }}</h3>
                        <p class="whitespace-pre-wrap" [innerHTML]="issue[$auditFinding.recommendation] | linkify"></p>
                      </div>
                    </div>
                  </div>
                  @if (hasCreateAttachmentPermissions && data.userOrigin === data.evalOrigin) {
                    <div class="row mb-4" id="add-attachment-section">
                      <div class="col-12">
                        <h3 class="h4 fw-bold">
                          <label for="{{ $auditIssueFields.files }}">{{ 'label_upload_attachments' | translate }}</label>
                        </h3>
                        <div>{{ 'manual_audit_issue_supported_files' | translate }}</div>
                        <div
                          class="upload-button mt-2"
                          [ngClass]="{ 'on-drag-enter': isDragAreaActive === true }"
                          (drop)="onDropFiles($event)"
                          (dragenter)="onDragEnterOrOver($event)"
                          (dragover)="onDragEnterOrOver($event)"
                          (dragleave)="onDragLeave($event)"
                        >
                          <button type="button" (click)="triggerFileUploadDialog()">
                            <span class="d-block d-sm-none">{{ 'select_files' | translate }}</span
                            ><span class="d-none d-md-block">{{ 'select_or_drag_and_drop_files' | translate }}</span>
                          </button>
                          <div class="input-type-file" aria-hidden="true">
                            <input
                              type="file"
                              multiple="multiple"
                              value=""
                              accept=".csv,.xlsx,.xls,.jpg,.jpeg,.png,{{ mimeType.CSV }},{{ mimeType.XLSX }},{{
                                mimeType.JPEG
                              }},{{ mimeType.PNG }}"
                              id="{{ $auditIssueFields.files }}"
                              name="{{ $auditIssueFields.files }}"
                              tabindex="-1"
                              [attr.data-selected-files-length]="0"
                              [(ngModel)]="attachmentsUploader"
                              class="form-element"
                              (change)="uploadFiles($event)"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                  @if (issue[$auditFinding.screenshot]?.length > 0 || issue[$auditFinding.attachment]?.length > 0) {
                    <div class="row">
                      <div class="col-12">
                        <h3 class="h4 fw-bold">{{ 'screenshots_and_attachments' | translate }}</h3>
                        <div class="overflow-auto">
                          @if (issue[$auditFinding.attachment]?.length > 0 || issue[$auditFinding.screenshot]?.length > 0) {
                            <div class="screenshots-attachments mt-2 mb-5 pb-2">
                              @for (screenshot of issue[$auditFinding.screenshot]; track screenshot) {
                                <app-attachment-thumbnail
                                  fileName="{{ screenshot }}"
                                  fileDownloadLink="{{ screenshot }}"
                                  [isInternal]="false"
                                ></app-attachment-thumbnail>
                              }
                              @for (attachment of issue[$auditFinding.attachment]; track attachment[$uploads._id]) {
                                <app-attachment-thumbnail
                                  fileName="{{ attachment[$uploads.fileOriginalName] }}"
                                  fileThumbnailPath="{{ attachment?.[$uploads.thumbnail]?.[$uploads.fileRelativePath] }}"
                                  fileDownloadLink="{{ getDownloadLinkForFile(attachment) }}"
                                  fileRelativePath="{{ attachment[$uploads.fileRelativePath] }}"
                                  [fileUploadId]="attachment[$uploads._id]"
                                  [isEditable]="hasStatusEditPermissions"
                                  (updateAttachment)="updateExistingAttachment($event)"
                                  [isRemovable]="hasDeleteAttachmentPermissions"
                                  (removeAttachment)="removeExistingAttachment(attachment)"
                                  [isInternal]="true"
                                  [altText]="attachment.altText"
                                ></app-attachment-thumbnail>
                              }
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </div>
              <div class="row">
                <div class="col-md-12 d-flex flex-column">
                  <div class="row">
                    <div class="col-md-12">
                      <h2 id="finding-comments-heading" class="section-title">{{ 'comments_and_activity_label' | translate }}</h2>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12">
                      @if ((linkedCommentsFlag$ | async) && linkedTask && (canReadLinkedTaskComments$ | async)) {
                        <ds-tabs
                          data-test-id="linked-comments-tabs"
                          tablistLabelledBy="finding-comments-heading"
                          (activeIdChange)="onLinkedCommentsTabChange($event)"
                        >
                          <ds-tab [label]="'linked_comments_tab_finding' | translate">
                            <app-manual-audit-issue-comment-section
                              [digitalPropertyId]="digitalPropertyId"
                              [workspaceId]="workspaceId"
                              [evaluationScanId]="evaluationScanId"
                              [manualAuditFindingId]="issue[$auditFinding._id]"
                              [manualAuditIssueId]="issue[$auditFinding.issueId]"
                              [userOrigin]="data.userOrigin"
                              [evaluationOrigin]="data.evalOrigin"
                            >
                            </app-manual-audit-issue-comment-section>
                          </ds-tab>
                          <ds-tab [label]="'linked_comments_tab_task' | translate">
                            <app-linked-task-comments
                              data-test-id="linked-comments-panel"
                              [workspaceId]="workspaceId"
                              [projectId]="linkedTask[$task.projectId]"
                              [taskId]="linkedTask[$task._id]"
                            ></app-linked-task-comments>
                          </ds-tab>
                        </ds-tabs>
                      } @else {
                        <app-manual-audit-issue-comment-section
                          [digitalPropertyId]="digitalPropertyId"
                          [workspaceId]="workspaceId"
                          [evaluationScanId]="evaluationScanId"
                          [manualAuditFindingId]="issue[$auditFinding._id]"
                          [manualAuditIssueId]="issue[$auditFinding.issueId]"
                          [userOrigin]="data.userOrigin"
                          [evaluationOrigin]="data.evalOrigin"
                        >
                        </app-manual-audit-issue-comment-section>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      <!-- rule details card -->
      <div class="col-xl-4" id="ruleDetails">
        @if (ruleReport) {
          <app-manual-rule-details [ruleReport]="ruleReport"></app-manual-rule-details>
        }
      </div>
    </div>
  </div>
}
